import { DEFAULT_THROUGH_HOLE_VIA_UID, isCopperLayer, matchLayerIdentifier, } from "@buildwithflux/models";
import { isViaSegmentConnectingLayer } from "./utils";
/**
 * Check whether a connection between the specified layers is possible using the given via segments
 */
function isRoutePossible(combination, connectedLayers) {
    const segments = combination.flatMap((config) => config.segments);
    const [firstConnectedLayer, ...remainingConnectedLayers] = connectedLayers;
    if (!firstConnectedLayer)
        return false;
    // Get the first segment index to start the route check
    let nextSegmentIndex = segments.findIndex((segment) => isViaSegmentConnectingLayer(segment, firstConnectedLayer));
    if (nextSegmentIndex < 0)
        return false;
    // Pre-check if there are any more connected layers to check
    if (!remainingConnectedLayers.length)
        return true;
    // Traverse through next segments and connected layer to find a route
    // Break the loop if the there are no more connected layers to check
    // or if the `nextSegmentIndex` goes out of bounds
    while (segments[nextSegmentIndex] && remainingConnectedLayers.length) {
        const prevSegment = segments[nextSegmentIndex - 1];
        const nextSegment = segments[nextSegmentIndex];
        const nextLayerOrder = remainingConnectedLayers[0];
        // If the previous segment is disconnected with this segment or,
        // the start layer of this segment is greater than the next layer,
        // then the config is not routable
        if ((prevSegment && prevSegment.endLayer !== nextSegment.startLayer) ||
            nextSegment.startLayer > nextLayerOrder) {
            break;
        }
        // If this segment connects to the next layer, move on to the next layer
        // Don't increase the `nextSegmentIndex` just now since that segment might
        // just connect the next connectingLayer as well
        if (isViaSegmentConnectingLayer(nextSegment, nextLayerOrder)) {
            remainingConnectedLayers.shift();
            continue;
        }
        // Otherwise, check against the next segment and continue the loop
        nextSegmentIndex++;
    }
    // If no layers remain to be checked, the config is routable
    return remainingConnectedLayers.length === 0;
}
/**
 * Checks the compatibility of a combination of via configs
 */
function isValidCombination(combination) {
    const segments = combination.flatMap((config) => config.segments);
    // Check if the combination has any segments at all
    if (!segments.length)
        return false;
    // A single segment will always be compatible
    if (segments.length === 1)
        return true;
    // Sort all segments from based on their startLayer
    segments.sort((a, b) => a.startLayer - b.startLayer);
    // Loop through all the segments and check if these segments are compatible.
    // If they are move to the next
    for (let idx = 0; idx < segments.length - 1; idx++) {
        const currSegment = segments[idx];
        const nextSegment = segments[idx + 1];
        // This condition assumes that segment.startLayer < segment.endLayer (strictly)
        if (nextSegment.startLayer >= currSegment.endLayer)
            continue;
        else
            return false;
    }
    return true;
}
/**
 * Gets the average order of a combination of via configs
 */
function getCombinationAvgOrder(combination) {
    // We are checking upstream if the combination is empty, so we can safely assume it's not
    // and go ahead confidently with the assumption that `combination.length` is non-zero
    return combination.reduce((acc, curr) => acc + curr.order, 0) / combination.length;
}
/**
 * Check if setA is a subset of setB
 */
function isSubset(setA, setB) {
    for (const item of setA) {
        if (!setB.has(item))
            return false;
    }
    return true;
}
/**
 * Get the "better" combination of via configs between two combinations,
 * based on their average order, length, and the first differing via config
 */
function getBetterCombination(combinationA, combinationB) {
    // 0. Sanity check: If either combination is empty, return the other
    if (!combinationA.length)
        return combinationB;
    if (!combinationB.length)
        return combinationA;
    // 1. Check if either combination is a subset of the other
    const combinationConfigSetA = new Set(combinationA);
    const combinationConfigSetB = new Set(combinationB);
    if (isSubset(combinationConfigSetA, combinationConfigSetB))
        return combinationA;
    if (isSubset(combinationConfigSetB, combinationConfigSetA))
        return combinationB;
    // 2. Check against their average order
    const avgOrderA = getCombinationAvgOrder(combinationA);
    const avgOrderB = getCombinationAvgOrder(combinationB);
    if (avgOrderA < avgOrderB)
        return combinationA;
    else if (avgOrderA > avgOrderB)
        return combinationB;
    // 3. Check against their total segment count
    const totalSegmentsA = combinationA.reduce((sum, config) => sum + config.segments.length, 0);
    const totalSegmentsB = combinationB.reduce((sum, config) => sum + config.segments.length, 0);
    if (totalSegmentsA < totalSegmentsB)
        return combinationA;
    else if (totalSegmentsA > totalSegmentsB)
        return combinationB;
    // 4. Check against difference of the first differing via config
    for (let j = 0; j < combinationA.length; j++) {
        // Their lengths are equal, so we can safely assume that the orders exist
        const orderA = combinationA[j].order;
        const orderB = combinationB[j].order;
        if (orderA < orderB)
            return combinationA;
        else if (orderA > orderB)
            return combinationB;
    }
    // This should never happen
    return combinationA;
}
/**
 * Sorts the selected via configs based on connected layer orders
 */
function sortViaConfigs(viaConfigs, _connectedLayerOrders) {
    const sortedViaConfigs = [];
    // Create a copy of the connected layer orders so we can safely mutate it
    const connectedLayerOrders = [..._connectedLayerOrders];
    while (connectedLayerOrders.length) {
        // Get the next connecting via config and remove it from the list
        const layerOrder = connectedLayerOrders.shift();
        // Find the config who's (any) segment connects to the current layer
        // and hasn't already been sorted/pushed to the list
        const connectingConfig = viaConfigs.find((viaConfig) => viaConfig.segments.some((segment) => isViaSegmentConnectingLayer(segment, layerOrder)) &&
            !sortedViaConfigs.includes(viaConfig));
        if (connectingConfig) {
            // If the last segment of the found config doesn't directly connect to the next layer,
            // add it to the list of connected layers
            const lastSegmentEndLayer = connectingConfig.segments.at(-1).endLayer;
            if (!connectedLayerOrders.includes(lastSegmentEndLayer)) {
                connectedLayerOrders.unshift(lastSegmentEndLayer);
            }
            // Push the connecting via config to the sorted list
            sortedViaConfigs.push(connectingConfig);
        }
    }
    return sortedViaConfigs;
}
/**
 * Gets the selected via type based on the user's enabled via configs and connected layers
 * @param enabledViaConfigUids List of via configs enabled by the user
 * @param connectedLayers List of layers that are to be connected
 * @param stackupViaConfigs Available via configs defined in the stackup
 * @param stackupLayers Available layers defined in the stackup
 * @returns List of via config uids that are selected to create the connection
 */
export function getSelectedViaConfigs(enabledViaConfigUids, connectedLayers, stackupViaConfigs, stackupLayers) {
    // Get user-enabled via configs and sort them based on their priority
    const sortedViaConfigs = stackupViaConfigs
        .filter((viaConfig) => enabledViaConfigUids.includes(viaConfig.name) || enabledViaConfigUids.includes(viaConfig.uid))
        .sort((a, b) => (a.order === b.order ? a.segments.length - b.segments.length : a.order - b.order));
    // Get the default through-hole via config and remove it from the list of sorted via configs
    const defaultThroughHoleConfigIndex = sortedViaConfigs.findIndex((viaConfig) => viaConfig.uid === DEFAULT_THROUGH_HOLE_VIA_UID);
    const [defaultThroughHoleConfig] = sortedViaConfigs.splice(defaultThroughHoleConfigIndex, 1);
    // These are the layers where the finally selected via config should allow for annular ring
    // Layer order = Copper layer index + 1
    const copperLayers = stackupLayers.filter(isCopperLayer);
    const connectedLayerOrders = connectedLayers.includes("All")
        ? // If "All" is selected, then all copper layers are connected
            copperLayers.map((_, i) => i + 1)
        : // Otherwise, only the selected layers are connected, and sort them ascending order (top to bottom)
            connectedLayers
                .map((connectedLayer) => copperLayers.findIndex((copperLayer) => matchLayerIdentifier(copperLayer, connectedLayer)) + 1)
                .sort((a, b) => a - b);
    // The default through-hole via config will always satisfy the connected layer requirements, so include it by default
    let currentBestCombination = defaultThroughHoleConfig ? [defaultThroughHoleConfig] : [];
    // Conduct a BFS search to find the best combination of via configs that satisfies the connected layer requirements
    // A BFS search will ensure that we find the combination with the least number of via configs first
    // The queue will contain a list of indices of the via configs in the sortedViaConfigs array
    // To start with, add all single-element combinations to the queue
    const queue = [];
    queue.push(...sortedViaConfigs.map((config) => [config]));
    const validAndRoutableSegmentsMap = new Map();
    while (queue.length) {
        const candidateCombination = queue.shift();
        if (!candidateCombination || !candidateCombination.length)
            continue;
        // Get the chosen via configs and their segments
        const combinationSegments = candidateCombination.flatMap(({ segments }) => segments);
        // Create deterministic cache key from sorted segment keys
        const combinationSegmentsKey = combinationSegments
            .map((segment) => segment.key)
            .sort()
            .join(",");
        const cachedResult = validAndRoutableSegmentsMap.get(combinationSegmentsKey);
        if (cachedResult === true) {
            // This combination is known to be valid and routable, update selection
            currentBestCombination = getBetterCombination(currentBestCombination, candidateCombination);
            continue;
        }
        else if (cachedResult === false) {
            // This combination is known to be invalid, skip processing
            continue;
        }
        // If the current combination is valid, check for route possibility
        if (isValidCombination(candidateCombination)) {
            if (isRoutePossible(candidateCombination, connectedLayerOrders)) {
                validAndRoutableSegmentsMap.set(combinationSegmentsKey, true);
                // Check and update the selected via configs
                // We're doing this here to reduce space complexity by not storing all possible combinations (2^n)
                currentBestCombination = getBetterCombination(currentBestCombination, candidateCombination);
            }
            else {
                // If the current combination is valid but does not connect all layers,
                // there might be a routable combination that can be found by adding more via configs
                for (const config of sortedViaConfigs) {
                    if (!candidateCombination.includes(config)) {
                        queue.push([...candidateCombination, config]);
                    }
                }
            }
        }
        else {
            // This combination is not valid (segments overlap), cache the result to avoid recomputation
            validAndRoutableSegmentsMap.set(combinationSegmentsKey, false);
        }
    }
    // Filter for sanity and safety
    currentBestCombination = currentBestCombination.filter(Boolean);
    // Sort the selected via configs based on their priority
    currentBestCombination = sortViaConfigs(currentBestCombination, connectedLayerOrders);
    return currentBestCombination;
}
//# sourceMappingURL=selectedViaConfigs.js.map